import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "./BaseModal.scss";

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  titleIcon?: React.ReactNode;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
  className?: string;
  overlayClassName?: string;
  onKeyDown?: (e: KeyboardEvent) => void;
  /** Off by default: a window closes only with its X, Vazgeç or Esc
   *  (owner 2026-10-15), never with a stray click outside. */
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  showCloseButton?: boolean;
}

// Module-scoped counter to manage scroll locks on stacked modals correctly
let activeModalsCount = 0;

/** An open menu inside a window owns Escape: the first Esc closes the menu,
 *  the next one the window. Menus that are not the common Dropdown mark
 *  themselves with `data-modal-esc-guard` while open. */
export const ESC_GUARD_SELECTOR =
  ".dropdown-portal, .dropdown-trigger.is-open, [data-modal-esc-guard]";

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  titleIcon,
  headerActions,
  children,
  footer,
  maxWidth = "1000px",
  className = "",
  overlayClassName = "",
  onKeyDown,
  closeOnOverlayClick = false,
  closeOnEsc = true,
  showCloseButton = true,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Tracks whether the mouse press that precedes an overlay click started on
  // the overlay itself. Prevents a drag-select that starts inside the modal and
  // is released on the overlay from being treated as a "click outside" -> close.
  const mouseDownOnOverlayRef = useRef(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200); // Matches the animation duration
  }, [onClose]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Sync state if isOpen is externally toggled
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  // Scroll lock body with stack tracking
  useEffect(() => {
    if (!isOpen) return;

    activeModalsCount++;
    if (activeModalsCount === 1) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      activeModalsCount = Math.max(0, activeModalsCount - 1);
      if (activeModalsCount === 0) {
        document.body.style.overflow = "";
      }
    };
  }, [isOpen]);

  // Focus management: focus on open and restore on close
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      
      // Auto focus modal container or close button for screen readers
      if (modalRef.current) {
        // If there's a close button, focus it, otherwise focus the modal container itself
        const closeBtn = modalRef.current.querySelector(".base-modal__close") as HTMLElement;
        if (closeBtn) {
          closeBtn.focus();
        } else {
          modalRef.current.focus();
        }
      }
    }

    return () => {
      if (previousActiveElement.current) {
        // Delay focus restoration slightly to prevent browser scroll jumping during animations
        const restoreEl = previousActiveElement.current;
        setTimeout(() => {
          if (restoreEl && typeof restoreEl.focus === "function") {
            restoreEl.focus();
          }
        }, 50);
      }
    };
  }, [isOpen]);

  // Keyboard events: Escape to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (!closeOnEsc) return;
        // Open dropdowns own Escape (Dropdown also stopImmediatePropagation).
        // Modal closes on the next Esc after the menu is gone.
        if (document.querySelector(ESC_GUARD_SELECTOR)) {
          return;
        }
        e.preventDefault();
        handleClose();
      } else if (onKeyDown) {
        onKeyDown(e);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, onKeyDown, closeOnEsc, handleClose]);

  // Keyboard focus trapping inside the modal
  useEffect(() => {
    if (!isOpen) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]'
      );

      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [isOpen]);

  if (!isOpen) return null;

  // Render to document.body using React Portals
  return createPortal(
    <div
      className={`base-modal-overlay ${overlayClassName} ${isClosing ? "closing" : ""}`}
      onMouseDown={(e) => {
        mouseDownOnOverlayRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (
          closeOnOverlayClick &&
          e.target === e.currentTarget &&
          mouseDownOnOverlayRef.current
        ) {
          handleClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className={`base-modal ${className} ${isClosing ? "closing" : ""}`}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={typeof title === "string" ? "base-modal-title" : undefined}
        tabIndex={-1}
      >
        {(title || titleIcon || headerActions || showCloseButton) && (
          <div className="base-modal__header">
            <div className="base-modal__title-group">
              {titleIcon && <div className="base-modal__title-icon">{titleIcon}</div>}
              {title && (
                <h3 id={typeof title === "string" ? "base-modal-title" : undefined}>
                  {title}
                </h3>
              )}
            </div>
            <div className="base-modal__header-actions">
              {headerActions}
              {showCloseButton && (
                <button
                  className="base-modal__close"
                  onClick={handleClose}
                  aria-label="Close modal"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="base-modal__content">{children}</div>

        {footer && <div className="base-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default BaseModal;
